// FILE: src/components/QuestionFactory/PipelineRunDetail.js
//
// Phase 2a — show items in a pipeline run + the run's audit events.
// Read-only for now. Phase 3 adds Approve / Reject / Edit actions.

import React, { useCallback, useEffect, useState } from 'react';
import {
  getPipelineRun,
  listPipelineItems,
  listPipelineEvents,
  publishItem,
  rejectItem,
} from '../../services/questionFactoryService';
import PipelineItemDetailModal from './PipelineItemDetailModal';

const STYLES = {
  wrapper: { marginTop: '12px' },
  back: {
    padding: '6px 14px',
    background: 'transparent',
    border: '1px solid #555',
    borderRadius: '4px',
    color: '#ccc',
    cursor: 'pointer',
    fontSize: '0.85rem',
    marginBottom: '12px',
  },
  card: {
    background: '#15152a',
    border: '1px solid #2a2a4a',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '16px',
    color: '#eee',
  },
  title: { fontSize: '1rem', fontWeight: 600, marginBottom: '8px' },
  meta:  { fontSize: '0.8rem', color: '#aab', marginBottom: '4px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' },
  th:    { textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #2a2a4a', color: '#aab', fontWeight: 600 },
  td:    { padding: '8px 10px', borderBottom: '1px solid #20203a', color: '#ddd', verticalAlign: 'top' },
  empty: { padding: '16px', color: '#888', fontStyle: 'italic' },
  preview: { color: '#ddd', maxWidth: '420px', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' },
  error: { color: '#ff6b6b', fontSize: '0.85rem', marginTop: '8px' },
  sectionTitle: { fontSize: '0.85rem', fontWeight: 600, color: '#aab', textTransform: 'uppercase', letterSpacing: '0.6px', marginTop: '12px', marginBottom: '8px' },
};

function questionPreview(raw) {
  if (!raw) return '—';
  const q = typeof raw === 'object' ? raw.question : null;
  return typeof q === 'string' ? q : '(no question text)';
}

function formatDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function PipelineRunDetail({ runId, onBack, onOpenInEditor, onQuestionStoreInvalidated }) {
  const [run, setRun]       = useState(null);
  const [items, setItems]   = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoad]  = useState(true);
  const [error, setError]   = useState(null);
  const [activeItem, setActiveItem] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);

  const reload = useCallback(async () => {
    setLoad(true);
    setError(null);
    try {
      const [r, it, ev] = await Promise.all([
        getPipelineRun(runId),
        listPipelineItems({ runId, limit: 500 }),
        listPipelineEvents({ runId, limit: 100 }),
      ]);
      setRun(r);
      setItems(it);
      setEvents(ev);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoad(false);
    }
  }, [runId]);

  useEffect(() => { reload(); }, [reload]);

  return (
    <div style={STYLES.wrapper} data-testid="qf-run-detail">
      <button style={STYLES.back} onClick={onBack} data-testid="qf-run-detail-back">← Back to runs</button>

      <div style={STYLES.card}>
        <div style={STYLES.title}>Run {runId.slice(0, 8)}…</div>
        {loading && <div style={STYLES.meta}>Loading…</div>}
        {error && <div style={STYLES.error}>{error}</div>}
        {run && !loading && (
          <>
            <div style={STYLES.meta}>Exam: <b>{run.exam_id}</b> · Source: <b>{run.source}</b> · Status: <b>{run.status}</b></div>
            <div style={STYLES.meta}>Created: {formatDate(run.created_at)} by {run.created_by}</div>
            <div style={STYLES.meta}>
              Provider model: <b>{run.provider_model || '—'}</b>
              {run.estimated_cost_usd != null && Number(run.estimated_cost_usd) > 0 && (
                <> · Est. cost: <b>${Number(run.estimated_cost_usd).toFixed(4)}</b></>
              )}
              {run.input_tokens > 0 && (
                <> · Tokens: <b>{run.input_tokens.toLocaleString()}</b> in / <b>{run.output_tokens.toLocaleString()}</b> out</>
              )}
            </div>
            <div style={{ ...STYLES.meta, display: 'flex', flexWrap: 'wrap', gap: '14px' }}>
              <span title="Stage 1">📥 Generated: <b>{run.generated_count}</b></span>
              <span title="Stage 2">✅ Validated: <b>{run.validated_count}</b></span>
              <span title="Stage 3">🤝 Adjudicated: <b>{run.adjudicated_count}</b></span>
              <span title="Stage 4">🏷️ Tagged: <b>{run.tagged_count}</b></span>
              <span title="Stage 5">🔁 Deduped: <b>{run.deduped_count}</b></span>
              <span title="Stage 6">📦 Staged: <b>{run.staged_count}</b></span>
              <span title="Stage 7">🚀 Published: <b>{run.published_count}</b></span>
              <span title="Rejected">❌ Rejected: <b>{run.rejected_count}</b></span>
            </div>
            {run.error_message && <div style={STYLES.error}>Error: {run.error_message}</div>}
          </>
        )}
      </div>

      <div style={STYLES.sectionTitle}>Items ({items.length})</div>
      {(() => {
        const adjudicatedIds  = items.filter((it) => it.pipeline_status === 'adjudicated').map((it) => it.id);
        const autoEligibleIds = items.filter((it) =>
          it.pipeline_status === 'adjudicated' && it.publish_recommendation === 'auto_publish'
        ).map((it) => it.id);
        const selected = Array.from(selectedIds).filter((id) => adjudicatedIds.includes(id));

        async function runBulk(handler, ids, label) {
          if (ids.length === 0) return;
          setBulkBusy(true); setBulkError(null); setBulkResult(null);
          let ok = 0; let fail = 0; const errors = [];
          for (const id of ids) {
            try { await handler({ itemId: id }); ok++; }
            catch (e) { fail++; errors.push(`${id.slice(0,8)}: ${e.message || e}`); }
          }
          setBulkBusy(false);
          setBulkResult({ ok, fail, label, errors });
          await reload();
          setSelectedIds(new Set());
          // If this was a publish (handler === publishItem), invalidate the cert's
          // in-memory question store so admins see the new rows in the Editor
          // without a hard refresh.
          if (ok > 0 && handler === publishItem
              && typeof onQuestionStoreInvalidated === 'function'
              && run?.exam_id) {
            onQuestionStoreInvalidated(run.exam_id);
          }
        }

        return (
          <div style={{ display: 'flex', gap: '10px', marginBottom: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <button data-testid="qf-bulk-publish-selected"
                    disabled={bulkBusy || selected.length === 0}
                    style={{
                      padding: '6px 12px', fontSize: '0.82rem',
                      background: '#2e5a3a', border: '1px solid #4a9a5a',
                      borderRadius: '6px', color: '#fff',
                      cursor: bulkBusy || selected.length === 0 ? 'not-allowed' : 'pointer',
                      opacity: bulkBusy || selected.length === 0 ? 0.55 : 1,
                    }}
                    onClick={() => runBulk(publishItem, selected, 'Publish selected')}>
              {bulkBusy ? 'Working…' : `Publish selected (${selected.length})`}
            </button>
            <button data-testid="qf-bulk-publish-auto"
                    disabled={bulkBusy || autoEligibleIds.length === 0}
                    style={{
                      padding: '6px 12px', fontSize: '0.82rem',
                      background: '#3a5a3a', border: '1px solid #5a8a5a',
                      borderRadius: '6px', color: '#fff',
                      cursor: bulkBusy || autoEligibleIds.length === 0 ? 'not-allowed' : 'pointer',
                      opacity: bulkBusy || autoEligibleIds.length === 0 ? 0.55 : 1,
                    }}
                    onClick={() => {
                      if (!window.confirm(`Publish ${autoEligibleIds.length} auto-eligible item(s) into the live question bank?`)) return;
                      runBulk(publishItem, autoEligibleIds, 'Publish all auto-eligible');
                    }}>
              Publish all auto-eligible ({autoEligibleIds.length})
            </button>
            <button data-testid="qf-bulk-reject-selected"
                    disabled={bulkBusy || selected.length === 0}
                    style={{
                      padding: '6px 12px', fontSize: '0.82rem',
                      background: '#5a2e2e', border: '1px solid #9a4a4a',
                      borderRadius: '6px', color: '#fff',
                      cursor: bulkBusy || selected.length === 0 ? 'not-allowed' : 'pointer',
                      opacity: bulkBusy || selected.length === 0 ? 0.55 : 1,
                    }}
                    onClick={() => {
                      if (!window.confirm(`Reject ${selected.length} selected item(s)?`)) return;
                      runBulk(rejectItem, selected, 'Reject selected');
                    }}>
              Reject selected
            </button>
            <span style={{ fontSize: '0.75rem', color: '#888' }}>
              Checkboxes are enabled only on items at <code>adjudicated</code> status.
            </span>
            {bulkError && <span style={{ fontSize: '0.8rem', color: '#ff6b6b' }}>{bulkError}</span>}
            {bulkResult && (
              <span style={{ fontSize: '0.8rem', color: bulkResult.fail === 0 ? '#7be88c' : '#e8d97b' }}>
                {bulkResult.label}: {bulkResult.ok} ok, {bulkResult.fail} failed
              </span>
            )}
          </div>
        );
      })()}
      <table style={STYLES.table}>
        <thead>
          <tr>
            <th style={STYLES.th} title="Select adjudicated items for bulk actions">Sel</th>
            <th style={STYLES.th}>Stage</th>
            <th style={STYLES.th}>Validation</th>
            <th style={STYLES.th}>Consensus</th>
            <th style={STYLES.th}>Dup</th>
            <th style={STYLES.th}>Question preview</th>
            <th style={STYLES.th}>Created</th>
          </tr>
        </thead>
        <tbody>
          {!loading && items.length === 0 && (
            <tr><td style={STYLES.empty} colSpan={7}>No drafts in this run yet.</td></tr>
          )}
          {items.map((it) => {
            const overall100 = it.consensus_score != null ? Math.round(Number(it.consensus_score) * 10) : null;
            const pubColor = it.publish_recommendation === 'auto_publish' || it.publish_recommendation === 'stage_for_review'
              ? '#7be88c'
              : it.publish_recommendation === 'manual_review' ? '#e8d97b'
              : it.publish_recommendation === 'reject' ? '#ff9b9b'
              : '#888';
            const selectable = it.pipeline_status === 'adjudicated';
            const isSelected = selectedIds.has(it.id);
            return (
            <tr key={it.id} data-testid={`qf-item-${it.id}`}
                style={{ cursor: 'pointer' }}
                onClick={() => setActiveItem(it)}>
              <td style={STYLES.td} onClick={(e) => e.stopPropagation()}>
                {selectable && (
                  <input type="checkbox" data-testid={`qf-item-checkbox-${it.id}`}
                         checked={isSelected}
                         onChange={(e) => {
                           const next = new Set(selectedIds);
                           if (e.target.checked) next.add(it.id); else next.delete(it.id);
                           setSelectedIds(next);
                         }} />
                )}
              </td>
              <td style={STYLES.td}>
                {it.pipeline_status}
                {it.publish_recommendation && (
                  <div style={{ fontSize: '0.7rem', color: pubColor, marginTop: '2px' }}>
                    {it.publish_recommendation.replace(/_/g, ' ')}
                  </div>
                )}
              </td>
              <td style={STYLES.td}>{it.validation_score != null ? Number(it.validation_score).toFixed(1) : '—'}</td>
              <td style={STYLES.td}>{overall100 != null ? `${overall100}/100` : '—'}</td>
              <td style={STYLES.td}>{it.duplicate_similarity ?? '—'}</td>
              <td style={STYLES.td}><div style={STYLES.preview}>{questionPreview(it.raw_payload)}</div></td>
              <td style={STYLES.td}>{formatDate(it.created_at)}</td>
            </tr>
            );
          })}
        </tbody>
      </table>

      <div style={STYLES.sectionTitle}>Audit trail</div>
      <table style={STYLES.table}>
        <thead>
          <tr>
            <th style={STYLES.th}>When</th>
            <th style={STYLES.th}>Event</th>
            <th style={STYLES.th}>Actor</th>
          </tr>
        </thead>
        <tbody>
          {!loading && events.length === 0 && (
            <tr><td style={STYLES.empty} colSpan={3}>No events recorded.</td></tr>
          )}
          {events.map((e) => (
            <tr key={e.id}>
              <td style={STYLES.td}>{formatDate(e.created_at)}</td>
              <td style={STYLES.td}>{e.event_type}</td>
              <td style={STYLES.td}>{e.actor}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {activeItem && (
        <PipelineItemDetailModal
          item={activeItem}
          onClose={() => setActiveItem(null)}
          onOpenInEditor={typeof onOpenInEditor === 'function' && run
            ? (questionId) => onOpenInEditor({ examId: run.exam_id, questionId })
            : undefined}
          onPublished={typeof onQuestionStoreInvalidated === 'function' && run
            ? () => onQuestionStoreInvalidated(run.exam_id)
            : undefined}
        />
      )}
    </div>
  );
}
